/**
 * area-utils.ts — Utilidades de presentación para AreaLaboratorio.
 *
 * Funciones puras sin dependencias React.
 * Sincronizado con AreaLaboratorio en backend/apps/ordenes/models.py
 */

import type { AreaLaboratorio, ContadorArea } from "@/hooks/use-procesamiento";

// =============================================================================
// ETIQUETAS
// =============================================================================

const AREA_LABELS: Record<AreaLaboratorio, string> = {
  HEMATOLOGIA: "Hematología",
  QUIMICA: "Química Clínica",
  SEROLOGIA: "Serología",
  INMUNOLOGIA: "Inmunología",
  URINALISIS: "Urianálisis",
  COAGULACION: "Coagulación",
  MICROBIOLOGIA: "Microbiología",
  PARASITOLOGIA: "Parasitología",
  HORMONAS: "Hormonas",
  MARCADORES: "Marcadores Tumorales",
  DROGAS: "Drogas y Toxicología",
  ESPECIAL: "Pruebas Especiales",
};

/**
 * Retorna la etiqueta en español para un área de laboratorio.
 */
export function getAreaLabel(area: AreaLaboratorio): string {
  return AREA_LABELS[area] ?? area;
}

// =============================================================================
// ICONOS (nombre del icono de lucide-react)
// =============================================================================

const AREA_ICONS: Record<AreaLaboratorio, string> = {
  HEMATOLOGIA: "Droplets",
  QUIMICA: "FlaskConical",
  SEROLOGIA: "Shield",
  INMUNOLOGIA: "ShieldCheck",
  URINALISIS: "TestTube",
  COAGULACION: "Activity",
  MICROBIOLOGIA: "Microscope",
  PARASITOLOGIA: "Bug",
  HORMONAS: "Zap",
  MARCADORES: "Target",
  DROGAS: "AlertTriangle",
  ESPECIAL: "Star",
};

/**
 * Retorna el nombre del icono lucide-react para un área de laboratorio.
 */
export function getAreaIconName(area: AreaLaboratorio): string {
  return AREA_ICONS[area] ?? "FlaskConical";
}

// =============================================================================
// ORDENAMIENTO
// =============================================================================

/**
 * Ordena un array de ContadorArea por conteo descendente (más pendientes primero).
 * Ordenamiento estable: áreas con el mismo conteo mantienen su orden original.
 *
 * @param areas - Array de ContadorArea a ordenar
 * @returns Nuevo array ordenado (no muta el original)
 */
export function sortAreasByCount(areas: ContadorArea[]): ContadorArea[] {
  return [...areas].sort((a, b) => b.conteo - a.conteo);
}

/**
 * Filtra áreas que tienen al menos un examen pendiente.
 */
export function filterAreasConPendientes(areas: ContadorArea[]): ContadorArea[] {
  return areas.filter((a) => a.conteo > 0);
}

/**
 * Retorna el total de exámenes pendientes sumando todos los contadores.
 */
export function getTotalPendientes(areas: ContadorArea[]): number {
  return areas.reduce((sum, a) => sum + a.conteo, 0);
}
